import type { PlayerRatingSettings } from '../types/playerRating';
import { DEFAULT_PLAYER_RATING_SETTINGS } from '../types/playerRating';

export interface PlayerRatingValidationResult {
  valid: boolean;
  error?: string;
  normalized?: number;
}

export function normalizePlayerRatingSettings(
  settings: Partial<PlayerRatingSettings> | null | undefined
): PlayerRatingSettings {
  return {
    enabled: settings?.enabled ?? DEFAULT_PLAYER_RATING_SETTINGS.enabled,
    label: settings?.label?.trim() || DEFAULT_PLAYER_RATING_SETTINGS.label,
    min: settings?.min ?? DEFAULT_PLAYER_RATING_SETTINGS.min,
    max: settings?.max ?? DEFAULT_PLAYER_RATING_SETTINGS.max,
    step: settings?.step ?? DEFAULT_PLAYER_RATING_SETTINGS.step,
  };
}

function roundToStep(value: number, step: number): number {
  const decimals = Math.max(0, (String(step).split('.')[1] ?? '').length);
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function validatePlayerRating(
  rating: number,
  settings: Pick<PlayerRatingSettings, 'min' | 'max' | 'step'>
): PlayerRatingValidationResult {
  if (!Number.isFinite(rating)) {
    return { valid: false, error: 'Rating must be a number' };
  }

  const normalized = roundToStep(rating, settings.step);
  if (normalized < settings.min || normalized > settings.max) {
    return {
      valid: false,
      error: `Rating must be between ${settings.min} and ${settings.max}`,
    };
  }

  const remainder = Math.abs((normalized - settings.min) / settings.step - Math.round((normalized - settings.min) / settings.step));
  if (remainder > 1e-6) {
    return {
      valid: false,
      error: `Rating must use step increments of ${settings.step}`,
    };
  }

  return { valid: true, normalized };
}

export function formatPlayerRating(rating: number, decimals = 1): string {
  return rating.toFixed(decimals);
}

export interface RatingSeedParticipant {
  id: string;
  seed?: number | null;
  playerRating?: number | null;
}

/**
 * Assigns seed numbers to participants without an explicit seed, ordered by player rating descending.
 * Higher rating receives a lower (better) seed number. Explicit seeds are preserved.
 */
export function resolveSeedsFromRatings<T extends RatingSeedParticipant>(
  participants: T[],
  startSeed = 1
): Map<string, number> {
  const result = new Map<string, number>();
  const withoutSeed = participants.filter((p) => p.seed == null && p.playerRating != null);
  const sorted = [...withoutSeed].sort((a, b) => {
    const ra = a.playerRating ?? -Infinity;
    const rb = b.playerRating ?? -Infinity;
    if (ra !== rb) return rb - ra;
    return a.id.localeCompare(b.id);
  });

  const usedSeeds = new Set(
    participants
      .map((p) => p.seed)
      .filter((seed): seed is number => seed != null)
  );

  let nextSeed = startSeed;
  for (const participant of sorted) {
    while (usedSeeds.has(nextSeed)) {
      nextSeed += 1;
    }
    result.set(participant.id, nextSeed);
    usedSeeds.add(nextSeed);
    nextSeed += 1;
  }

  return result;
}

export function effectiveParticipantSeed<T extends RatingSeedParticipant>(
  participant: T,
  computedSeeds: Map<string, number>
): number {
  if (participant.seed != null) return participant.seed;
  return computedSeeds.get(participant.id) ?? Number.MAX_SAFE_INTEGER;
}
